import {
  Badge,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from '@elkaso-app/web-design';
import { ISupplierSearchData, useSearchSuppliersApi } from 'apis/supplier/use-search-suppliers-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheck, FiSearch, FiUser, FiX } from 'react-icons/fi';
import { useDebounce } from '../../../hooks/use-debounce';

interface SupplierAsyncSelectProps {
  value?: ISupplierSearchData | null;
  onChange: (supplier: ISupplierSearchData | null) => void;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  error?: string;
  isDisabled?: boolean;
}

export const SupplierAsyncSelect: React.FC<SupplierAsyncSelectProps> = ({
  value,
  onChange,
  isRequired = false,
  label = 'Supplier',
  placeholder = 'Search suppliers by name...',
  error,
  isDisabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const selectedBgColor = useColorModeValue('blue.50', 'blue.900');

  // API call
  const { data, isLoading, isFetching } = useSearchSuppliersApi(debouncedSearchTerm, {
    enabled: debouncedSearchTerm.length >= 2,
  });

  const suppliers = useMemo(() => data?.data || [], [data]);

  // Update input value when value changes externally
  useEffect(() => {
    if (value) {
      setInputValue(value.name);
      setSearchTerm('');
    } else {
      setInputValue('');
    }
  }, [value]);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      setInputValue(inputValue);
      setSearchTerm(inputValue);

      if (!isOpen && inputValue.length >= 2) {
        setIsOpen(true);
      }

      // Clear selection if user is typing
      if (value && inputValue !== value.name) {
        onChange(null);
      }
    },
    [value, onChange, isOpen]
  );

  const handleSupplierSelect = useCallback(
    (supplier: ISupplierSearchData) => {
      onChange(supplier);
      setInputValue(supplier.name);
      setSearchTerm('');
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setInputValue('');
    setSearchTerm('');
    setIsOpen(false);
  }, [onChange]);

  const handleInputFocus = useCallback(() => {
    if (inputValue.length >= 2 && !value) {
      setIsOpen(true);
    }
  }, [inputValue.length, value]);

  const handleInputBlur = useCallback(() => {
    // Delay closing to allow clicking on options
    setTimeout(() => setIsOpen(false), 200);
  }, []);

  const showDropdown = isOpen && (isLoading || isFetching || suppliers.length > 0);
  const showNoResults =
    isOpen && !isLoading && !isFetching && suppliers.length === 0 && debouncedSearchTerm.length >= 2;

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      <FormLabel>{label}</FormLabel>
      <Box position='relative'>
        <HStack spacing={0}>
          <Input
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            isDisabled={isDisabled}
            pr={value ? '40px' : '30px'}
            borderRightRadius={value ? '0' : 'md'}
          />
          {value && (
            <IconButton
              aria-label='Clear selection'
              icon={<FiX />}
              size='sm'
              variant='ghost'
              colorScheme='gray'
              onClick={handleClear}
              borderLeftRadius='0'
              isDisabled={isDisabled}
            />
          )}
        </HStack>

        {/* Selected Supplier Info */}
        {value && (
          <Box mt={2} p={3} bg={selectedBgColor} borderRadius='md' border='1px solid' borderColor='blue.200'>
            <HStack spacing={3}>
              <Box p={2} bg='blue.100' borderRadius='full' color='blue.600'>
                <FiUser size={16} />
              </Box>
              <VStack align='start' spacing={1} flex='1'>
                <HStack spacing={2}>
                  <Text fontWeight='semibold' fontSize='sm' color='blue.800'>
                    {value.name}
                  </Text>
                  <Badge colorScheme='blue' size='sm'>
                    ID: {value.id}
                  </Badge>
                  {value.isActive !== undefined && (
                    <Badge colorScheme={value.isActive ? 'green' : 'red'} size='sm'>
                      {value.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  )}
                </HStack>
                {value.email && (
                  <Text fontSize='xs' color='gray.600'>
                    📧 {value.email}
                  </Text>
                )}
                {value.landLineNumber && (
                  <Text fontSize='xs' color='gray.600'>
                    📞 {value.landLineNumber}
                  </Text>
                )}
              </VStack>
              <IconButton
                aria-label='Selected'
                icon={<FiCheck />}
                size='sm'
                colorScheme='green'
                variant='solid'
                isDisabled
              />
            </HStack>
          </Box>
        )}

        {/* Dropdown */}
        {(showDropdown || showNoResults) && (
          <Box
            position='absolute'
            top='100%'
            left={0}
            right={0}
            zIndex={1000}
            bg={bgColor}
            border='1px solid'
            borderColor={borderColor}
            borderRadius='md'
            shadow='lg'
            maxHeight='300px'
            overflowY='auto'
            mt={1}>
            {/* Loading State */}
            {(isLoading || isFetching) && (
              <Flex align='center' justify='center' p={4}>
                <Spinner size='sm' mr={2} />
                <Text fontSize='sm' color='gray.500'>
                  Searching suppliers...
                </Text>
              </Flex>
            )}

            {/* No Results */}
            {showNoResults && (
              <Flex align='center' justify='center' p={4}>
                <FiSearch size={16} color='gray' style={{ marginRight: 8 }} />
                <Text fontSize='sm' color='gray.500'>
                  No suppliers found for &quot;{debouncedSearchTerm}&quot;
                </Text>
              </Flex>
            )}

            {/* Supplier Options */}
            {suppliers.map((supplier) => (
              <Box
                key={supplier.id}
                p={3}
                cursor='pointer'
                _hover={{ bg: hoverBgColor }}
                borderBottom='1px solid'
                borderColor={borderColor}
                onClick={() => handleSupplierSelect(supplier)}>
                <HStack spacing={3}>
                  <Box p={2} bg='gray.100' borderRadius='full' color='gray.600'>
                    <FiUser size={14} />
                  </Box>
                  <VStack align='start' spacing={1} flex='1'>
                    <HStack spacing={2}>
                      <Text fontWeight='medium' fontSize='sm'>
                        {supplier.name}
                      </Text>
                      <Badge colorScheme='gray' size='sm'>
                        ID: {supplier.id}
                      </Badge>
                      {supplier.isActive !== undefined && (
                        <Badge colorScheme={supplier.isActive ? 'green' : 'red'} size='xs'>
                          {supplier.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </HStack>
                    <HStack spacing={4}>
                      {supplier.email && (
                        <Text fontSize='xs' color='gray.500'>
                          📧 {supplier.email}
                        </Text>
                      )}
                      {supplier.landLineNumber && (
                        <Text fontSize='xs' color='gray.500'>
                          📞 {supplier.landLineNumber}
                        </Text>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
};
